import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import SavingsIcon from "@mui/icons-material/Savings";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  buscarNotasEcredacApuracao,
  exportarNotasEcredacApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro, { BuscaFiltro } from "../components/SelectFiltro";
import { sumarizacaoEmAndamento, formatarCnpj } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

/**
 * TXM-418/TXM-475 — colunas/filtros/dashboard seguem EXATAMENTE o card TXM-475 e o
 * protótipo (apuracao_icms_detalhes.html, painel #panelEcredac): grão de 1 linha por
 * NOTA/NCM (não por competência+categoria — essa é a materialização do fechamento
 * mensal oficial, `preencher_ecredacsimplificada`/aba de fechamento, que NÃO é o que essa
 * tela mostra). Fonte: relatorios.consultar_ecredacsimplificada_notas, consulta ao vivo
 * (mesmo padrão do drill-down de Lançamentos CAT-42), com a MESMA classificação de fluxo
 * (VENDA/DEVOLUÇÃO por CFOP) e fórmula (ICMS Suportado/Valor Confronto/ICMS Gera) da view
 * real `ecredac5` de produção (colada pelo usuário em 2026-09-15).
 *
 * O botão "Lançamentos" (TXM-479) abre a tela de classificação Insumo/Uso/Consumo por nota
 * (LancamentosEcredac) — classificação padrão viria do NCM cadastrado na Tabela NCM
 * (TXM-594, ainda não existe: toda nota nasce "Não classificado"), com override manual por
 * documento persistido em localStorage, igual ao protótipo (ecredac-classificacao-store.js).
 *
 * "Baixar arquivo digital" (2026-09-15, pedido explícito fora do protótipo/card — TXM-475/
 * TXM-479 não citam esse botão, só o CAT-42 tem equivalente) gera o leiaute OFICIAL do
 * Sistema Simplificado de Apuração do Crédito Acumulado (Anexo II da Portaria CAT-207/2009
 * — `relatorios.gerar_arquivo_digital_ecredacsimplificada`, ver
 * ecredacsimplificada_arquivo_digital.sql), mesmo mecanismo assíncrono via Kafka do CAT-42
 * (`tipo: "arquivo_digital"`, já genérico no endpoint de exportação). A entrega em si (poll +
 * download automático) foi corrigida em 2026-09-16 — até então o job era enfileirado e o
 * arquivo ficava preso no disco do worker. Desde 2026-10-02 o acompanhamento é pelo
 * `correlationId` (status + progresso por mês), via `useExportacaoAssincrona`.
 *
 * "Exportar CSV" (2026-10-05): exporta a grade "Créditos por nota" JÁ FILTRADA pela tela
 * (CNPJ/competência/fluxo/crédito), mas agora pelo backend (POST .../ecredac-notas/exportar,
 * síncrono — sem Kafka/job rastreado, a mesma consulta ao vivo já é rápida o bastante), que
 * reaplica os mesmos filtros e grava só na pasta relatorios. Antes era só client-side
 * (Blob + download do navegador), sem nenhum rastro no servidor — por isso veio fora do
 * padrão dos outros exports da apuração (fila/pasta relatorios, sem download).
 *
 * "Atualizar créditos" (2026-09-16): o card TXM-475 descreve este botão como "stub do
 * REFRESH MATERIALIZED VIEW, conforme roteiro TXM-466" — TXM-466 foi cancelado e esta
 * arquitetura não tem view materializada (a consulta já é sempre ao vivo, ver comentário
 * acima). Por isso o botão não recalcula nada no banco — não existe cache pra invalidar.
 * Em vez de deixá-lo permanentemente inerte, ele repete a MESMA consulta ao vivo
 * (buscarNotasEcredacApuracao) e substitui os dados na tela, com feedback visual (spinner +
 * snackbar de sucesso/erro). Funcionalmente é um "refresh manual" honesto: não existe
 * staleness a corrigir hoje, mas se este texto envelhecer e algum dia migrarmos parte da
 * consulta para algo com cache, o botão já dispara o caminho certo (refetch) sem precisar
 * de novo trabalho de UI.
 */
function calcularTotais(linhas) {
  let totalGera = 0;
  const competencias = new Set();
  for (const l of linhas) {
    if (l.fluxo === "VENDA" && l.icmsGera > 0) totalGera += l.icmsGera;
    competencias.add(l.competencia);
  }
  return {
    totalIcmsGera: totalGera,
    totalNotas: linhas.length,
    totalCompetencias: competencias.size,
  };
}

export default function AbaEcredac({ apuracaoId, onTotaisChange, bare }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [busca, setBusca] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [fluxoFiltro, setFluxoFiltro] = useState("");
  const [creditoFiltro, setCreditoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);
  const [atualizandoCreditos, setAtualizandoCreditos] = useState(false);
  const [exportandoCsv, setExportandoCsv] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarNotasEcredacApuracao(apuracaoId);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [apuracaoId]);

  const cnpjOpcoes = useMemo(() => {
    const distintos = Array.from(new Set(linhas.map((l) => l.cnpj))).sort();
    return [
      { value: "", label: "Todos" },
      ...distintos.map((c) => ({ value: c, label: formatarCnpj(c) })),
    ];
  }, [linhas]);

  const competenciaOpcoes = useMemo(() => {
    const distintas = Array.from(new Set(linhas.map((l) => l.competencia))).sort();
    return [{ value: "", label: "Todas" }, ...distintas.map((c) => ({ value: c, label: c }))];
  }, [linhas]);

  const fluxoOpcoes = [
    { value: "", label: "Todos" },
    { value: "VENDA", label: "Venda" },
    { value: "DEVOLUCAO", label: "Devolução" },
  ];

  const creditoOpcoes = [
    { value: "", label: "Todos" },
    { value: "SIM", label: "Sim (icms_gera > 0)" },
    { value: "NAO", label: "Não" },
  ];

  const linhasFiltradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhas.filter((l) => {
      if (cnpjFiltro && l.cnpj !== cnpjFiltro) return false;
      if (q && !l.ncm.toLowerCase().includes(q) && !l.documento.toLowerCase().includes(q)) return false;
      if (competenciaFiltro && l.competencia !== competenciaFiltro) return false;
      if (fluxoFiltro && l.fluxo !== fluxoFiltro) return false;
      if (creditoFiltro === "SIM" && !(l.icmsGera > 0)) return false;
      if (creditoFiltro === "NAO" && l.icmsGera > 0) return false;
      return true;
    });
  }, [linhas, cnpjFiltro, busca, competenciaFiltro, fluxoFiltro, creditoFiltro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  function limparFiltros() {
    setCnpjFiltro("");
    setBusca("");
    setCompetenciaFiltro("");
    setFluxoFiltro("");
    setCreditoFiltro("");
  }

  // 2026-10-05 — era download client-side (exportarCsvClientSide), sem nenhum rastro no
  // servidor; agora o backend reaplica os mesmos filtros e grava só na pasta relatorios.
  async function handleExportar() {
    setExportandoCsv(true);
    try {
      const res = await exportarNotasEcredacApuracao(apuracaoId, {
        cnpj: cnpjFiltro,
        busca,
        competencia: competenciaFiltro,
        fluxo: fluxoFiltro,
        credito: creditoFiltro,
      });
      setInfoSnack({
        open: true,
        msg: res.sucesso ? "Arquivo gerado na pasta de relatórios." : res.mensagem,
        severity: res.sucesso ? "success" : "error",
      });
    } finally {
      setExportandoCsv(false);
    }
  }

  /**
   * TXM-479/CAT-42 — "Baixar arquivo digital" gera o leiaute OFICIAL do Sistema
   * Simplificado de Apuração do Crédito Acumulado (Anexo II da Portaria CAT-207/2009),
   * mesmo mecanismo assíncrono via Kafka já usado pelo CAT-42 (handleBaixarArquivoDigital em
   * DetalheCat42/Conteudo.js): enfileira e o arquivo é entregue depois (não é download
   * direto do navegador) — `tipo: "arquivo_digital"` já é aceito genericamente pelo endpoint
   * de exportação, só o worker Go precisava de um branch novo pra esse motor.
   */
  // 2026-10-02 — acompanhamento por correlationId (status/progresso por mês) no lugar do
  // polling por nome de arquivo; ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacaoArquivoDigital = useExportacaoAssincrona({
    apuracaoId,
    chave: "arquivo_digital",
    enfileirar: () => exportarSumarizacaoApuracao(apuracaoId, { tipo: "arquivo_digital" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  /**
   * "Atualizar créditos" — ver comentário de topo do arquivo. Não existe view materializada
   * pra dar REFRESH; o botão repete a mesma consulta ao vivo usada no carregamento inicial
   * e substitui `linhas`, dando ao usuário uma ação real (não um clique que só mostra um
   * aviso) e feedback visível de que os dados exibidos são os mais recentes agora mesmo.
   */
  async function handleAtualizarCreditos() {
    setAtualizandoCreditos(true);
    try {
      const res = await buscarNotasEcredacApuracao(apuracaoId);
      if (!res.sucesso) {
        setInfoSnack({
          open: true,
          msg: res.mensagem || "Não foi possível atualizar os créditos.",
          severity: "error",
        });
        return;
      }
      setLinhas(res.linhas);
      setErro("");
      setInfoSnack({ open: true, msg: "Créditos atualizados.", severity: "success" });
    } finally {
      setAtualizandoCreditos(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
    <ApuracaoCardShell
      bare={bare}
      conteudoComScrollProprio
      Icone={SavingsIcon}
      titulo="Créditos por nota (Apuração Simplificada, Portaria CAT 207/2009)"
      tituloSx={{ fontSize: "1.15rem" }}
      acaoHeader={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
            {linhasFiltradas.length} nota{linhasFiltradas.length !== 1 ? "s" : ""} no filtro
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/relatorios/apuracoes/${apuracaoId}/detalhe/icms/lancamentos-ecredac`)}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
          >
            Lançamentos
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={exportandoCsv}
            onClick={handleExportar}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
          >
            {exportandoCsv ? "Exportando…" : "Exportar CSV"}
          </Button>
          <BotaoExportacaoProgresso
            progresso={exportacaoArquivoDigital}
            rotulo="Baixar arquivo digital"
            variant="outlined"
            size="small"
            onClick={() => exportacaoArquivoDigital.iniciar()}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={atualizandoCreditos}
            onClick={handleAtualizarCreditos}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
          >
            {atualizandoCreditos ? "Atualizando…" : "Atualizar créditos"}
          </Button>
        </Box>
      }
      filtros={
        <>
          <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
          <BuscaFiltro
            label="NCM ou documento"
            placeholder="Pesquisar código ou chave"
            value={busca}
            onChange={setBusca}
          />
          <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} />
          <SelectFiltro label="Fluxo" value={fluxoFiltro} onChange={setFluxoFiltro} options={fluxoOpcoes} />
          <SelectFiltro label="Gera crédito" value={creditoFiltro} onChange={setCreditoFiltro} options={creditoOpcoes} />
          <Button
            variant="outlined"
            size="small"
            onClick={limparFiltros}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
          >
            Limpar filtros
          </Button>
        </>
      }
      paginacao={
        linhasFiltradas.length > 0 && (
          <Pagination
            paginationKey="aba-ecredac"
            data={linhasFiltradas}
            filteredData={linhasPagina}
            setFilteredData={setLinhasPagina}
          />
        )
      }
    >
      {linhasFiltradas.length === 0 ? (
        <p className={styles.emptyMsg}>Nenhuma nota encontrada com os filtros informados.</p>
      ) : (
        <div style={{ margin: "8px 0 0", overflow: "auto" }}>
        <table className={tableStyles.accordion_table}>
          <thead>
            <tr>
              <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol1}`}>CNPJ</th>
              <th scope="col">Competência</th>
              <th scope="col">Fluxo</th>
              <th scope="col">Documento / Chave</th>
              <th scope="col">NCM</th>
              <th scope="col" style={NUM_TH}>ICMS Suportado</th>
              <th scope="col" style={NUM_TH}>Valor Confronto</th>
              <th scope="col" style={NUM_TH}>ICMS Gera</th>
            </tr>
          </thead>
          <tbody>
            {linhasPagina.map((linha, idx) => (
              <tr key={`${linha.documento}-${linha.ncm}-${idx}`}>
                <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol1}`}>{formatarCnpj(linha.cnpj)}</td>
                <td><strong>{linha.competencia}</strong></td>
                <td>{linha.fluxo === "VENDA" ? "Venda" : "Devolução"}</td>
                <td className={tableStyles.mono || ""} title={linha.documento}>
                  {linha.documento.length > 15 ? `${linha.documento.slice(0, 15)}…` : linha.documento}
                </td>
                <td>{linha.ncm || "—"}</td>
                <td style={NUM_TD}>{formatarMoedaBRL(linha.icmsSuportado)}</td>
                <td style={NUM_TD}>{formatarMoedaBRL(linha.valorConfronto)}</td>
                <td style={{ ...NUM_TD, color: linha.icmsGera > 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                  {formatarMoedaBRL(linha.icmsGera)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </ApuracaoCardShell>

    <Snackbar
      open={infoSnack.open}
      autoHideDuration={4000}
      onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
    >
      <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
    </Snackbar>
    </>
  );
}
